import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import octopusInstance from "../../services/octopus/octopusInstance";
import { updateFundsFromWebSocket, refreshFundsAction } from "../../store/actions/funds";

function useFundsUpdates() {
  const dispatch = useDispatch();
  const currentFunds = useSelector(state => state.funds.fundDetails);
  const fundsRef = useRef(currentFunds);
  const handlerRef = useRef(null);
  const setupAttempts = useRef(0);
  const auth = useSelector((state) => state.auth);
  const lastRefreshTimeRef = useRef(Date.now());
  const visibilityTimeoutRef = useRef(null);

  // Keep ref updated with latest funds data
  useEffect(() => {
    fundsRef.current = currentFunds;
  }, [currentFunds]);

  // Setup visibility change handler to refresh data when tab becomes visible
  useEffect(() => {
    const REFRESH_COOLDOWN = 10000; // 10 seconds minimum between refreshes
    let wasHidden = false; // Track if tab was previously hidden

    const handleVisibilityChange = () => {
      // Check if user is authenticated before making API calls
      const authToken = localStorage.getItem("auth_token");
      if (!authToken) {
        return; // Don't refresh if not logged in
      }

      // Only refresh if coming back from hidden state
      if (document.visibilityState === 'visible' && wasHidden) {
        const currentTime = Date.now();
        const timeInvisible = currentTime - lastRefreshTimeRef.current;
        
        // If tab was invisible for more than REFRESH_COOLDOWN, schedule a refresh
        if (timeInvisible > REFRESH_COOLDOWN) {
          // Clear any existing timeout
          if (visibilityTimeoutRef.current) {
            clearTimeout(visibilityTimeoutRef.current);
          }
          
          // Schedule a single refresh after a short delay to avoid multiple rapid refreshes
          visibilityTimeoutRef.current = setTimeout(() => {
            console.log('Funds: Refreshing data after tab became visible');
            // Force refresh by passing true parameter
            dispatch(refreshFundsAction(true));
            lastRefreshTimeRef.current = Date.now();
            visibilityTimeoutRef.current = null;
          }, 1000);
        }
        
        // Reset hidden state
        wasHidden = false;
      } else if (document.visibilityState === 'hidden') {
        // Set flag that tab was hidden
        wasHidden = true;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [dispatch]);

  const setupHandler = () => {
    try {
      const clientId = localStorage.getItem("login_id");
      const authToken = localStorage.getItem("auth_token");
      
      if (!clientId || !authToken) {
        console.log('Missing clientId or authToken for funds WebSocket');
        return;
      }

      // Force close any existing connection
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }

      // Configure WebSocket handler for funds updates
      handlerRef.current = octopusInstance.wsHandler({
        messageType: "FundsUpdate",
        subscriptionLocation: "funds",
        payload: {}
      });

      if (handlerRef.current) {
        handlerRef.current.subscribe((data) => {
          // Log received data for debugging
          console.log('Funds WebSocket data received:', data);
          
          if (!data || !data.msg) {
            console.log('Invalid funds data format received');
            return;
          }
          
          try {
            // Handle both array and object formats
            const fundsData = Array.isArray(data.msg) ? data.msg : [data.msg];
            
            if (fundsData.length > 0) {
              console.log('Processing funds update:', fundsData);
              dispatch(updateFundsFromWebSocket(fundsData));
              lastRefreshTimeRef.current = Date.now(); // Update the refresh time
            }
          } catch (error) {
            console.error('Error processing funds WebSocket data:', error);
          }
        });
        
        console.log('Funds WebSocket subscription established');
        return handlerRef.current;
      }
    } catch (error) {
      console.error('Error setting up funds WebSocket handler:', error);
    }
  };

  // Set up when auth state changes
  useEffect(() => {
    if (auth?.isLoggedIn) {
      setupHandler();
      // Also fetch initial funds data
      dispatch(refreshFundsAction(true));
    }
  }, [auth?.isLoggedIn, dispatch]);

  // Initial setup with retries
  useEffect(() => {
    const attemptConnection = () => {
      const authToken = localStorage.getItem("auth_token");
      const clientId = localStorage.getItem("login_id");
      
      if (authToken && clientId && !handlerRef.current && setupAttempts.current < 3) {
        setupAttempts.current += 1;
        console.log(`Attempt ${setupAttempts.current} to establish funds WebSocket connection`);
        setupHandler();
      }
    };

    // Initial attempt
    attemptConnection();

    // Retry after a short delay
    const retryTimer = setInterval(() => {
      if (!handlerRef.current && setupAttempts.current < 3) {
        attemptConnection();
      } else if (setupAttempts.current >= 3 && !handlerRef.current) {
        console.log('Max funds WebSocket connection attempts reached');
        clearInterval(retryTimer);
      }
    }, 1000);

    return () => {
      clearInterval(retryTimer);
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }
    };
  }, []);

  // Reconnect on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if user is authenticated before attempting reconnection
        const authToken = localStorage.getItem("auth_token");
        if (!authToken) {
          return; // Don't reconnect if not logged in
        }
        console.log('Page visible - refreshing funds WebSocket connection');
        setupAttempts.current = 0; // Reset attempts counter
        setupHandler();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
}

export default useFundsUpdates; 